import { Capacitor } from "@capacitor/core";
import { assertNever, getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import { SplashScreen } from "../common/SplashScreen.js";
import { CurrentUserError } from "./auth/CurrentUserError.js";
import { ImplicitFlow } from "./auth/ImplicitFlow.js";
import { InvalidSelectedUserError } from "./auth/InvalidSelectedUserError.js";
import { isValidOrganisationType } from "./auth/isValidOrganisationType.js";
import { SelectUserPage } from "./auth/SelectUserPage.js";
import { SignupClaimsOrganisationCreator } from "./auth/signup/SignupClaimsOrganisationCreator.js";
import { SignupGatePage } from "./auth/signup/SignupGatePage.js";
import { SignupPage } from "./auth/signup/SignupPage.js";
import { SessionExpiredError } from "./auth/SessionExpiredError.js";
import { getCreateNewOrganisationTokenProps } from "./auth/signupRefToken.js";
import { useAuth } from "./auth/useAuth.js";
import { useAvailableUsers } from "./auth/useAvailableUsers.js";
import { useSelectedUser } from "./auth/useSelectedUser.js";
import { useUserData } from "./auth/useUserData.js";
import { ErrorBoundary } from "./commons/ErrorBoundary.js";
import { useUrlSearchParams } from "./commons/hooks/useUrlSearchParams.js";
import {
  ORGANISATION_TYPE_URL_SEARCH_PARAM,
  REF_TOKEN_URL_SEARCH_PARAM,
} from "./constants.js";
import { OnboardingFlowModal } from "./features/organisations/onboarding/OnboardingFlowModal.js";
import { useOnboardingFlow } from "./features/organisations/onboarding/useOnboardingFlow.js";
import { InstallerRoutes } from "./installer-routes/Routes.js";
import { CrmLinkingInvitation } from "./invitation/CrmLinkingInvitation.js";
import { ContinueInvitationJoinPage } from "./invitation/continue/ContinueInvitationJoinPage.js";
import { ContinueInvitationPage } from "./invitation/continue/ContinueInvitationPage.js";
import { InvitationPage } from "./invitation/InvitationPage.js";
import { OrganisationMembershipInvitation } from "./invitation/OrganisationMembershipInvitation.js";
import { ProjectContracteeInvitation } from "./invitation/ProjectContracteeInvitation.js";
import { useInvitationTokenContext } from "./invitation/useInvitationTokenContext.js";
import { M1RedirectRoute } from "./main-routes/M1RedirectRoute.js";
import { MainRoutes } from "./main-routes/Routes.js";
import { SapSalesQuotationQuoteRedirectRoute } from "./main-routes/SapSalesQuotationQuoteRedirectRoute.js";
import { OrganisationPublicRoutes } from "./public-routes/OrganisationPublicRoutes.js";

export const AppRoutes = () => {
  const location = useLocation();
  const { isReady, isAuthenticated } = useAuth();

  const {
    token,
    tokenData: rawTokenData,
    isLoading: invitationTokenDataIsLoading,
  } = useInvitationTokenContext();
  const tokenData = getDataOrNull(rawTokenData ?? undefined);

  if (!isReady || invitationTokenDataIsLoading) {
    return <SplashScreen />;
  }

  if (
    !isAuthenticated &&
    token &&
    tokenData &&
    tokenData?.__typename !== "ReferralInfo"
  ) {
    return (
      <InvitationPage
        isAuthenticated={false}
        signupRole={
          tokenData.__typename === "ProjectContracteeInvitation"
            ? "CLIENT"
            : "CRAFTSMAN"
        }
      >
        {tokenData.__typename === "OrganisationMembershipInvitation" ||
        tokenData.__typename === "InvitationNoLongerOpen" ? (
          <OrganisationMembershipInvitation
            isAuthenticated={false}
            invitation={tokenData}
          />
        ) : tokenData.__typename === "ProjectContracteeInvitation" ? (
          <ProjectContracteeInvitation
            isAuthenticated={false}
            invitation={tokenData}
          />
        ) : tokenData.__typename === "CrmLinkingInvitation" ? (
          <CrmLinkingInvitation
            isAuthenticated={false}
            invitation={tokenData}
          />
        ) : (
          assertNever(tokenData)
        )}
      </InvitationPage>
    );
  }

  return (
    <Routes>
      {/* LEGACY REDIRECTS */}
      <Route
        path="/authenticate"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/login"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/signup"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/signup/craftsman"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/signup/client"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      {/* LEGACY REDIRECTS */}

      <Route index element={<SignupGatePage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/org/*" element={<OrganisationPublicRoutes />} />
      <Route path="/continue-invitation" element={<ContinueInvitationPage />} />
      <Route
        path="/continue-invitation/join"
        element={<ContinueInvitationJoinPage />}
      />
      <Route
        path={"*"}
        element={
          <ErrorBoundary>
            <AuthenticatedRoutes />
          </ErrorBoundary>
        }
      />
    </Routes>
  );
};

const AuthenticatedRoutes = () => {
  const { auth, isReady, isAuthenticated, belongsToGroup } = useAuth();
  const { selectedUserId, setSelectedUserId } = useSelectedUser();
  const {
    availableUsers,
    isLoading: availableUsersIsLoading,
    hasResult: availableUsersHasResult,
    refetch: refetchAvailableUsers,
  } = useAvailableUsers();
  const {
    token,
    tokenData: rawTokenData,
    isLoading: invitationTokenDataIsLoading,
  } = useInvitationTokenContext();
  const tokenData = getDataOrNull(rawTokenData ?? undefined);
  const navigate = useNavigate();

  const { urlSearchParams, setUrlSearchParams } = useUrlSearchParams();
  const organisationType =
    urlSearchParams.get(ORGANISATION_TYPE_URL_SEARCH_PARAM) ?? "CRAFTSMAN";
  if (!isValidOrganisationType(organisationType)) {
    throw new Error("Invalid organisation type");
  }

  // The signup hints survive the Keycloak round trip for the organisation
  // creation of a NEW account. Once an account works in one of its identities
  // they would only linger in its URL. Until one is selected they stay: the
  // identity selection is where an `org-creator` creates a further
  // organisation, whose craftsman/client switch lives in `ot`. The `refToken`
  // stays when an invitation page still reads it from there — only a referral
  // token has no reader for an account that already has an identity.
  const hasIdentity =
    isAuthenticated &&
    !availableUsersIsLoading &&
    availableUsers.length > 0 &&
    Boolean(selectedUserId);
  const shouldRemoveOrganisationType =
    hasIdentity && urlSearchParams.has(ORGANISATION_TYPE_URL_SEARCH_PARAM);
  const shouldRemoveRefToken =
    hasIdentity &&
    urlSearchParams.has(REF_TOKEN_URL_SEARCH_PARAM) &&
    tokenData?.__typename === "ReferralInfo";

  const { setOnboardingFlowStage } = useOnboardingFlow();
  // Set once the signup starts creating the account's organisation: from then
  // on every loading screen up to the wizard reads "Preparing account ...",
  // also while the new identity is listed but not yet selected and before the
  // wizard's stage has reached the URL. It ends once the user data has loaded:
  // from there the wizard's own loading state carries the message.
  const [preparingAccount, setPreparingAccount] = React.useState(false);
  const { t } = useTranslate(["Global"]);

  React.useEffect(() => {
    if (
      !selectedUserId &&
      availableUsers.length === 1 &&
      !belongsToGroup("org-creator")
    ) {
      setSelectedUserId(availableUsers[0].id);
    }
  }, [availableUsers, belongsToGroup, selectedUserId, setSelectedUserId]);

  React.useEffect(() => {
    const paramsToRemove: string[] = [];
    if (shouldRemoveOrganisationType) {
      paramsToRemove.push(ORGANISATION_TYPE_URL_SEARCH_PARAM);
    }
    if (shouldRemoveRefToken) {
      paramsToRemove.push(REF_TOKEN_URL_SEARCH_PARAM);
    }
    if (paramsToRemove.length > 0) {
      setUrlSearchParams({}, paramsToRemove, { replace: true });
    }
  }, [shouldRemoveOrganisationType, shouldRemoveRefToken, setUrlSearchParams]);

  React.useEffect(() => {
    if (isReady && !isAuthenticated) {
      if (Capacitor.isNativePlatform()) {
        navigate("/");
      } else {
        auth.login();
      }
    }
  }, [auth, isAuthenticated, isReady, navigate]);

  if (!isAuthenticated) {
    return <SplashScreen />;
  }

  if (invitationTokenDataIsLoading) {
    return <SplashScreen />;
  }

  if (
    selectedUserId &&
    !availableUsersIsLoading &&
    availableUsers.every(user => user.id !== selectedUserId)
  ) {
    return <InvalidSelectedUserError />;
  }

  if (
    token &&
    tokenData &&
    (tokenData.__typename === "OrganisationMembershipInvitation" ||
      tokenData.__typename === "InvitationNoLongerOpen")
  ) {
    return (
      <InvitationPage isAuthenticated={true}>
        <OrganisationMembershipInvitation
          invitation={tokenData}
          isAuthenticated={true}
        />
      </InvitationPage>
    );
  }

  // A failed identity query also leaves the list empty. The signup claims
  // stay in the token of everyone who registered through /signup, so an
  // existing account must not be taken for a new one and get a second
  // organisation created on its behalf — neither from the claims nor through
  // a project-contractee invitation.
  if (
    !availableUsersIsLoading &&
    availableUsers.length === 0 &&
    !availableUsersHasResult
  ) {
    return <CurrentUserError />;
  }

  if (
    !availableUsersIsLoading &&
    availableUsers.length === 0 &&
    token &&
    tokenData &&
    tokenData?.__typename === "ProjectContracteeInvitation" &&
    organisationType === "CLIENT"
  ) {
    return (
      <ImplicitFlow
        invitationToken={tokenData.invitationToken}
        organisationName={`${auth.idTokenParsed?.given_name ?? ""} ${auth.idTokenParsed?.family_name ?? ""}`}
      />
    );
  }

  if (!availableUsersIsLoading && availableUsers.length === 0) {
    return (
      <SignupClaimsOrganisationCreator
        {...getCreateNewOrganisationTokenProps(token, tokenData)}
        onCreationStarted={() => setPreparingAccount(true)}
        onComplete={userId => {
          setPreparingAccount(true);
          setOnboardingFlowStage("intro");
          setSelectedUserId(userId);
        }}
      />
    );
  }

  if (token && tokenData) {
    if (tokenData?.__typename === "ProjectContracteeInvitation") {
      return (
        <InvitationPage isAuthenticated={true} signupRole="CLIENT">
          <ProjectContracteeInvitation
            invitation={tokenData}
            isAuthenticated={true}
            availableUsers={availableUsers}
            availableUsersIsLoading={availableUsersIsLoading}
          />
        </InvitationPage>
      );
    } else if (tokenData?.__typename === "CrmLinkingInvitation") {
      return (
        <InvitationPage isAuthenticated={true}>
          <CrmLinkingInvitation
            invitation={tokenData}
            isAuthenticated={true}
            availableUsers={availableUsers}
            availableUsersIsLoading={availableUsersIsLoading}
          />
        </InvitationPage>
      );
    }
  }

  // The organisation selection is for choosing between identities: while the
  // first list is still loading, or while the only identity is being selected
  // (the effect above; right after the signup creation too), it would only
  // flash up.
  if (
    !selectedUserId &&
    ((availableUsersIsLoading && availableUsers.length === 0) ||
      (availableUsers.length === 1 && !belongsToGroup("org-creator")))
  ) {
    return (
      <SplashScreen
        message={
          preparingAccount
            ? t("Preparing account ...", { ns: "Global" })
            : undefined
        }
      />
    );
  }

  if (!selectedUserId) {
    return (
      <SelectUserPage
        availableUsers={availableUsers}
        availableUsersIsLoading={availableUsersIsLoading}
        onMount={refetchAvailableUsers}
      />
    );
  }

  return (
    <UserRoutes
      preparingAccount={preparingAccount}
      onAccountPrepared={() => setPreparingAccount(false)}
    />
  );
};

const UserRoutes = ({
  preparingAccount,
  onAccountPrepared,
}: {
  preparingAccount: boolean;
  onAccountPrepared: () => void;
}) => {
  const { t } = useTranslate(["Global"]);
  const { isAuthenticated } = useAuth();
  const { onboardingFlowStage, setOnboardingFlowStage } = useOnboardingFlow();
  const {
    isLoading: userDataIsLoading,
    currentUser,
    appViewerRole,
  } = useUserData();
  const m1RedirectMatch = useMatch(
    `/partner/m1plus/:m1AccountId/quote/:m1OfferNumber`
  );
  const sapSalesQuotationMatch = useMatch(
    `/partner/sap/sales-quotation/quote/:salesQuotationId`
  );

  React.useEffect(() => {
    if (preparingAccount && !userDataIsLoading) {
      onAccountPrepared();
    }
  }, [onAccountPrepared, preparingAccount, userDataIsLoading]);

  if (userDataIsLoading) {
    return (
      <SplashScreen
        message={
          preparingAccount || onboardingFlowStage === "intro"
            ? t("Preparing account ...", { ns: "Global" })
            : undefined
        }
      />
    );
  }

  if (!currentUser || !appViewerRole) {
    if (!isAuthenticated) {
      return <SessionExpiredError />;
    }

    return <CurrentUserError />;
  }

  if (
    m1RedirectMatch &&
    m1RedirectMatch.params.m1AccountId &&
    m1RedirectMatch.params.m1OfferNumber
  ) {
    return (
      <M1RedirectRoute
        m1AccountId={Number(m1RedirectMatch.params.m1AccountId)}
        m1OfferNumber={m1RedirectMatch.params.m1OfferNumber}
      />
    );
  }
  if (
    sapSalesQuotationMatch &&
    sapSalesQuotationMatch.params.salesQuotationId
  ) {
    return (
      <SapSalesQuotationQuoteRedirectRoute
        salesQuotationId={sapSalesQuotationMatch.params.salesQuotationId}
      />
    );
  }

  // use a separate router for installers
  if (appViewerRole === "INSTALLER") {
    return <InstallerRoutes />;
  }

  // The setup wizard renders in the same commit as the app: hosted by a page,
  // the page's chrome painted first and flashed up before the wizard.
  return (
    <>
      <MainRoutes />
      {onboardingFlowStage && (
        <OnboardingFlowModal
          stage={onboardingFlowStage}
          setStage={setOnboardingFlowStage}
        />
      )}
    </>
  );
};
